'use client'

import {
  Col,
  Row,
  Separator,
  Typography,
  type IconNames,
} from '@repo/ui/components'
import { twMerge } from 'tailwind-merge'
import { LandingFooterBrandSection } from './landing-footer-brand.section'
import {
  LandingFooterNavigationSection,
  type LandingFooterNavigationLinksConfig,
} from './landing-footer-navigation.section'
import { LandingFooterSocialSection } from './landing-footer-social.section'
import { LandingFooterLicenses } from './landing-footer.enamad'
import { useTranslate } from '../../hooks'

export type LandingFooterSocialLinksConfig = { href: string; icon: IconNames }[]

export type LandingFooterLicensesConfig = {
  enamad?: {
    url: string
    imageUrl: string
  }
  mojavez?: {
    url: string
  }
  nezam?: {
    url: string
  }
}
interface LandingFooterSectionProps {
  className?: string
  config?: {
    socialLinks: LandingFooterSocialLinksConfig
    licenses?: LandingFooterLicensesConfig
    links?: LandingFooterNavigationLinksConfig
  }
}

export const LandingFooterSection = (props: LandingFooterSectionProps) => {
  const { className, config } = props
  const { t } = useTranslate()

  return (
    <div
      className={twMerge(
        'bg-neutral-main-80 backdrop-blur-md p-md absolute bottom-0 left-0 w-full',
        className,
      )}
    >
      <div className='max-w-container-xl mx-auto'>
        {/* Desktop Layout */}
        <Col className='hidden md:flex items-center justify-between'>
          <Row className='p-md justify-between'>
            <LandingFooterBrandSection size='lg' className='p-md' />
            <Row className='w-fit gap-3xl'>
              {config?.socialLinks && (
                <LandingFooterSocialSection
                  gap='md'
                  iconSize='md'
                  socialLinks={config.socialLinks}
                />
              )}
              {config?.licenses && config?.socialLinks && (
                <div className='h-4xl'>
                  <Separator orientation='vertical' />
                </div>
              )}

              {config?.licenses && (
                <LandingFooterLicenses config={config.licenses} />
              )}
            </Row>
          </Row>
          <Row className='justify-between p-md ps-6xl'>
            <LandingFooterNavigationSection
              gap='4xl'
              direction='horizontal'
              links={config?.links || []}
            />
            <Typography
              color='text-neutral-surface-60'
              className='whitespace-nowrap'
            >
              {t('common_copyright', { company_name: t('company_full_name') })}
            </Typography>
          </Row>
        </Col>

        {/* Mobile Layout */}
        <Col className='md:hidden gap-lg'>
          <Row className='justify-between'>
            <LandingFooterBrandSection size='md' />
            {config?.socialLinks && (
              <LandingFooterSocialSection
                gap='md'
                iconSize='md'
                socialLinks={config?.socialLinks || []}
              />
            )}
          </Row>
          {config?.links && (
            <LandingFooterNavigationSection
              gap='2xl'
              direction='horizontal'
              mobileColumns={2}
              className='justify-center'
              links={config.links}
            />
          )}
          <Col className='justify-center items-center gap-md'>
            {config?.licenses && (
              <LandingFooterLicenses
                config={config.licenses}
                className='w-11 h-12'
              />
            )}
            <Typography
              color='text-neutral-surface-60'
              className='text-center whitespace-pre-line mb-xl'
            >
              {t('common_copyright', { company_name: t('company_full_name') })}
            </Typography>
          </Col>
        </Col>
      </div>
    </div>
  )
}
